import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import * as authApi from "./auth-api";

const { logger } = Sentry;

/**
 * Hook to sign in with email (magic link)
 */
export function useSignInWithEmailMutation() {
  return useMutation({
    mutationFn: ({
      email,
      inviteCode,
      redirectPath,
    }: {
      email: string;
      inviteCode?: string;
      /** CR-288: the page to come back to after sign-in. */
      redirectPath?: string;
    }) => authApi.signInWithEmail(email, inviteCode, redirectPath),
    onError: (error) => {
      logger.error("Auth: email sign in or sign up failed", {
        message: error.message,
      });
      Sentry.captureException(error);
    },
  });
}

/**
 * Hook to sign in with OAuth provider
 */
export function useSignInWithOAuthMutation() {
  return useMutation({
    mutationFn: ({
      provider,
      redirectPath,
    }: {
      provider: "github" | "google";
      /** CR-288: the page to come back to after sign-in. */
      redirectPath?: string;
    }) => authApi.signInWithOAuth(provider, redirectPath),
    onError: (error) => {
      logger.error("Auth: OAuth sign in failed", { message: error.message });
      Sentry.captureException(error);
    },
  });
}

/**
 * Hook to sign out the current user.
 *
 * After supabase.auth.signOut() resolves, Supabase broadcasts a SIGNED_OUT
 * event to all open tabs via BroadcastChannel / storage events. The
 * onAuthStateChange listener in __root.tsx picks this up and calls
 * router.invalidate(), which causes the _authenticated layout's beforeLoad
 * guard to re-run, find no session, and redirect every tab to /login.
 */
export function useSignOutMutation() {
  return useMutation({
    mutationFn: authApi.signOut,
    onError: (error) => {
      logger.error("Auth: sign out failed", { message: error.message });
      Sentry.captureException(error);
    },
  });
}
